/**
 * @file eligibility.ts
 *
 * The client-side mirror of the backend shop-eligibility rule (see
 * commerce-engine `shop-eligibility.ts`): a shop is allowed on the commerce plan,
 * or on a trial that has not yet expired. This is a UX gate only — it decides
 * whether to show the admin or the not-eligible screen. The backend re-checks
 * authoritatively on create, so a bypass here grants nothing. Plan slugs live in
 * {@link ./plans}.
 */

import { COMMERCE_PLAN, TRIAL_PLAN } from './plans';

/**
 * Decides whether a project may use the shop admin.
 *
 * @param plan - The project's plan slug.
 * @param trialExpirationDate - ISO timestamp the trial ends at, when on trial.
 * @param now - The current time (injected so the rule stays pure/testable).
 *
 * @returns `true` on the commerce plan, or a trial whose expiry is in the future.
 */
export function isShopEligible(
  plan: string | undefined,
  trialExpirationDate: string | undefined,
  now: Date,
): boolean {
  if (plan === COMMERCE_PLAN) {
    return true;
  }
  if (plan === TRIAL_PLAN) {
    const expiresAt = trialExpirationDate
      ? Date.parse(trialExpirationDate)
      : NaN;
    return Number.isFinite(expiresAt) && expiresAt > now.getTime();
  }
  return false;
}
